import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  signInWithPopup
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, firebaseReady, googleProvider, appleProvider, signInWithCredential, GoogleAuthProvider, OAuthProvider } from '../lib/firebase';
import { normalizeAllergies } from '../lib/preferencesOptions';
import { isNative, isIOS, isAndroid } from '../utils/platform';
import { trackEvent } from '../utils/analytics';
import { migrateLegacyToProfile } from '../lib/profileMigration';
import { useProfilesStore } from './profiles';
import { purgeLocalUserData } from '../lib/localData';

// Support et version de l'app posés sur le document users à la création :
// permet de suivre le funnel inscription -> livraison -> paiement par canal.
const SIGNUP_PLATFORM = isIOS ? 'ios' : (isAndroid ? 'android' : 'web');
const APP_VERSION = (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) || (typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : '');

// Prénom / nom de repli fournis par l'appelant (onboarding, wizard) quand le
// fournisseur social n'en donne pas.
function fallbackNames(options = {}) {
  return {
    firstName: String(options.firstName || '').trim(),
    lastName: String(options.lastName || '').trim()
  };
}

// Nom renvoyé par le plugin natif Apple : selon la version, il vit dans
// additionalUserInfo.profile (givenName/familyName, firstName/lastName,
// given_name/family_name) ou dans user.displayName.
function extractAppleNativeName(nativeResult) {
  const profile = nativeResult?.additionalUserInfo?.profile || {};
  const first = profile.givenName || profile.firstName || profile.given_name || '';
  const last = profile.familyName || profile.lastName || profile.family_name || '';
  if (first || last) return { firstName: String(first).trim(), lastName: String(last).trim() };
  const display = String(nativeResult?.user?.displayName || '').trim();
  if (!display) return { firstName: '', lastName: '' };
  const [f, ...rest] = display.split(' ');
  return { firstName: f, lastName: rest.join(' ') };
}

// Fermeture / blocage de la fenêtre d'auth sociale : seuls ces codes méritent
// le message « fenêtre fermée ». Les autres erreurs (compte existant avec un
// autre fournisseur, réseau...) passent par mapAuthError.
const POPUP_ERROR_CODES = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/popup-blocked'
]);

export const useAuthStore = defineStore('auth', () => {
  const isLoggedIn = ref(false);
  const user = ref(null);
  const isAuthReady = ref(false);
  let authUnsubscribe = null;

  // Migration paresseuse : s'exécute une fois par login. Echec silencieux pour
  // ne pas casser le login si Firestore est temporairement injoignable.
  async function _runProfileMigration() {
    try {
      if (!user.value?.uid) return;
      // Utilise le prénom du user comme nom du profil par défaut au lieu de "Moi".
      // Fallback sur la 1re partie de displayName si firstName absent (Google/Apple sign-in).
      const fallbackName =
        user.value.firstName
        || (user.value.displayName ? user.value.displayName.split(' ')[0] : '')
        || '';
      await migrateLegacyToProfile(user.value.uid, user.value.preferences, { name: fallbackName });
    } catch (e) {
      console.warn('[auth] profile migration failed (silent):', e?.message);
    }
  }

  function login(userData) {
    // Legacy mock function removal
    console.warn("Usage of mock login is deprecated. Please use loginWithEmail.");
  }

  async function logout() {
    try {
      useProfilesStore().unbind();
    } catch (e) {
      // store may not be initialized yet, fine
    }
    if (firebaseReady && auth) {
      // Sign out on native layer first (if applicable)
      if (isNative) {
        try {
          const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
          await FirebaseAuthentication.signOut();
        } catch (e) {
          console.warn('Native sign-out failed', e);
        }
      }
      // Sign out on web layer (Firebase JS SDK)
      await signOut(auth);
    }
    isLoggedIn.value = false;
    user.value = null;
    localStorage.removeItem('isLoggedIn');
    // RGPD : brouillons, profils invites et marqueurs de suivi ne survivent pas a la personne.
    purgeLocalUserData();
  }

  function mapAuthError(error) {
    console.warn("Auth Error Detail:", error.code, error.message);
    switch (error.code) {
      case 'auth/invalid-credential':
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return 'Email ou mot de passe incorrect.';
      case 'auth/invalid-email':
        return "L'adresse email n'est pas valide.";
      case 'auth/user-disabled':
        return 'Ce compte a été désactivé. Contacte le support.';
      case 'auth/email-already-in-use':
        return 'Cet email est déjà associé à un compte. Connecte-toi plutôt.';
      case 'auth/weak-password':
        return 'Le mot de passe doit contenir au moins 6 caractères.';
      case 'auth/too-many-requests':
        return 'Trop de tentatives échouées. Compte temporairement bloqué. Réessaie plus tard.';
      case 'auth/network-request-failed':
        return 'Problème de connexion internet. Vérifie ton réseau.';
      case 'auth/account-exists-with-different-credential':
        return 'Un compte existe déjà avec cet email. Connecte-toi avec ton mot de passe.';
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
      case 'auth/popup-blocked':
        return 'La fenêtre de connexion a été fermée ou bloquée.';
      default:
        // Handle generic 400 errors if they don't have a code
        if (error.message && error.message.includes('400')) {
             return "Données invalides. Vérifie ta saisie.";
        }
        return 'Une erreur est survenue (' + (error.code || 'unknown') + '). Réessaie.';
    }
  }

  // Erreur d'une connexion Google / Apple : message « fenêtre fermée » réservé
  // aux codes popup, sinon le message détaillé de mapAuthError.
  function mapSocialAuthError(error, providerLabel) {
    const code = error?.code || '';
    if (POPUP_ERROR_CODES.has(code)) {
      return `La fenêtre de connexion ${providerLabel} a été fermée ou bloquée.`;
    }
    // Annulation native (plugin Capacitor) : même famille que la popup fermée.
    if (/cancel/i.test(String(error?.message || '')) && !code) {
      return `La connexion ${providerLabel} a été annulée.`;
    }
    return mapAuthError(error || {});
  }

  async function loginWithEmail(email, password) {
    if (!firebaseReady || !auth) {
      throw new Error("Le service d'authentification est indisponible.");
    }
    
    if (!email || !password) {
        throw new Error("L'email et le mot de passe sont requis.");
    }

    try {
        const result = await signInWithEmailAndPassword(auth, email, password);
        const profile = await loadUserProfile(result.user.uid, result.user.email);
        isLoggedIn.value = true;
        user.value = profile;

        _runProfileMigration().catch(() => {});
        // Trigger Notification Permission Prompt
        enableNotifications({ promptIfNeeded: false }).catch(err => console.error("Notification setup failed", err));
    } catch (error) {
        throw new Error(mapAuthError(error));
    }
  }

  async function loginWithGoogle(options = {}) {
    if (!firebaseReady || !auth) {
      throw new Error("Le service d'authentification est indisponible.");
    }

    let result;
    try {
      if (isNative) {
        // Native iOS: use @capacitor-firebase/authentication for native Google Sign-In
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        const nativeResult = await FirebaseAuthentication.signInWithGoogle();
        // Bridge to Firebase JS SDK via signInWithCredential
        const credential = GoogleAuthProvider.credential(nativeResult.credential?.idToken);
        result = await signInWithCredential(auth, credential);
      } else {
        // Web: use popup-based Google Sign-In
        result = await signInWithPopup(auth, googleProvider);
      }
    } catch (popupError) {
       console.error("Google Auth Error", popupError);
       throw new Error(mapSocialAuthError(popupError, 'Google'));
    }

    try {
      // Check if user exists in Firestore
      const userDocRef = doc(db, 'users', result.user.uid);
      const snapshot = await getDoc(userDocRef);
      const isNewAccount = !snapshot.exists();

      if (!snapshot.exists()) {
        // New User: Create Profile. Prénom : celui de Google, sinon celui
        // connu de l'appelant (onboarding), sinon vide (l'email admin part
        // quand users.firstName est renseigné).
        const fallback = fallbackNames(options);
        const [displayFirst, ...lastNameParts] = String(result.user.displayName || '').trim().split(' ');
        const firstName = displayFirst || fallback.firstName;
        const lastName = displayFirst ? lastNameParts.join(' ') : fallback.lastName;

        const newProfile = {
          uid: result.user.uid,
          firstName: firstName || '',
          lastName: lastName || '',
          email: result.user.email,
          // Le rattachement entreprise (companyId) est pose par la callable
          // joinCompanyByCode apres l'inscription : les regles refusent toute
          // valeur posee par le client a la creation.
          companyId: null,
          type: 'b2c',
          notifications: {
            email: true,
            email_confirmations: true,
            email_promotions: false,
            push: true,
            push_commandes: true,
            push_promotions: true,
            push_rappels: true
          },
          preferences: {
            regime: 'Omnivore',
            allergies: normalizeAllergies(),
            aversions: ''
          },
          // Provenance de l'inscription (ex : 'compte_au_paiement' pour un compte
          // créé depuis la feuille de paiement, en sortie d'onboarding).
          acquisitionSource: options.acquisitionSource || '',
          platform: SIGNUP_PLATFORM,
          appVersion: APP_VERSION,
          createdAt: serverTimestamp()
        };

        await setDoc(userDocRef, newProfile);
        user.value = newProfile;
        trackEvent('sign_up', { method: 'google' });
      } else {
        // Existing User: Load Profile
        user.value = { uid: result.user.uid, email: result.user.email, ...snapshot.data() };
        
        // Load Company Metadata if applicable
        if (user.value.companyId && !user.value.companyName) {
            try {
                const compSnap = await getDoc(doc(db, 'companies', user.value.companyId));
                if (compSnap.exists()) {
                    user.value.companyName = compSnap.data().name;
                }
            } catch (e) { 
               // Silent fail for company name
            }
        }
      }

      isLoggedIn.value = true;
      _runProfileMigration().catch(() => {});
      enableNotifications({ promptIfNeeded: false }).catch(() => {});

      return { isNewAccount };
    } catch (error) {
       console.error("Google Auth Firestore Error", error);
       // Critical: If we can't get/create the profile, we must sign out to avoid inconsistent state
       await signOut(auth);
       isLoggedIn.value = false;
       user.value = null;

       if (error.code === 'unavailable' || error.message.includes('offline') || error.message.includes('Failed to load resource')) {
           throw new Error("Connexion impossible. Vérifie ta connexion ou désactive ton bloqueur de publicités (Firestore bloqué).");
       }
       throw error;
    }
  }

  async function loginWithApple(options = {}) {
    if (!firebaseReady || !auth) {
      throw new Error("Le service d'authentification est indisponible.");
    }

    let result;
    try {
      if (isNative) {
        // Native iOS: use @capacitor-firebase/authentication for native Apple Sign-In
        // skipNativeAuth: true is required for Apple + Firebase JS SDK combo (documented quirk)
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        const nativeResult = await FirebaseAuthentication.signInWithApple({
          skipNativeAuth: true,
          scopes: ['name', 'email'],
        });
        // Bridge to Firebase JS SDK via signInWithCredential
        const provider = new OAuthProvider('apple.com');
        const credential = provider.credential({
          idToken: nativeResult.credential?.idToken,
          rawNonce: nativeResult.credential?.nonce,
        });
        result = await signInWithCredential(auth, credential);
        // Attach native Apple name to result for profile creation
        // (Apple's displayName is only available from native, not from Firebase credential)
        const nativeName = extractAppleNativeName(nativeResult);
        if (nativeName.firstName) {
          result._nativeGivenName = nativeName.firstName;
          result._nativeLastName = nativeName.lastName || '';
        }
      } else {
        // Web: use popup-based Apple Sign-In
        result = await signInWithPopup(auth, appleProvider);
      }
    } catch (popupError) {
      console.error("Apple Auth Error", popupError);
      throw new Error(mapSocialAuthError(popupError, 'Apple'));
    }

    try {
      const userDocRef = doc(db, 'users', result.user.uid);
      const snapshot = await getDoc(userDocRef);
      const isNewAccount = !snapshot.exists();

      if (!snapshot.exists()) {
        // New User: Create Profile from Apple credentials
        // Apple only provides name on first sign-in, so we must capture it here
        // On native, the name comes from the Capacitor plugin result (nativeResult.user),
        // not from result.user.displayName which is often null with skipNativeAuth: true
        let appleFirstName = result.user.displayName?.split(' ')[0] || '';
        let appleLastName = result.user.displayName?.split(' ').slice(1).join(' ') || '';

        // Fallback: try to get name from the native Apple result if available
        if (!appleFirstName && result._nativeGivenName) {
          appleFirstName = result._nativeGivenName;
          appleLastName = result._nativeLastName || '';
        }
        // Dernier repli : prénom connu de l'appelant (onboarding). Apple ne
        // donne le nom qu'à la toute première autorisation.
        if (!appleFirstName) {
          const fallback = fallbackNames(options);
          appleFirstName = fallback.firstName;
          appleLastName = fallback.lastName;
        }

        const newProfile = {
          uid: result.user.uid,
          firstName: appleFirstName || '',
          lastName: appleLastName || '',
          email: result.user.email || '',
          // Rattachement entreprise via joinCompanyByCode apres l'inscription.
          companyId: null,
          type: 'b2c',
          notifications: {
            email: true,
            email_confirmations: true,
            email_promotions: false,
            push: true,
            push_commandes: true,
            push_promotions: true,
            push_rappels: true
          },
          preferences: {
            regime: 'Omnivore',
            allergies: normalizeAllergies(),
            aversions: ''
          },
          authProvider: 'apple',
          // Provenance de l'inscription (ex : 'compte_au_paiement' pour un compte
          // créé depuis la feuille de paiement, en sortie d'onboarding).
          acquisitionSource: options.acquisitionSource || '',
          platform: SIGNUP_PLATFORM,
          appVersion: APP_VERSION,
          createdAt: serverTimestamp()
        };

        await setDoc(userDocRef, newProfile);
        user.value = newProfile;
        trackEvent('sign_up', { method: 'apple' });
      } else {
        user.value = { uid: result.user.uid, email: result.user.email, ...snapshot.data() };

        if (user.value.companyId && !user.value.companyName) {
          try {
            const compSnap = await getDoc(doc(db, 'companies', user.value.companyId));
            if (compSnap.exists()) user.value.companyName = compSnap.data().name;
          } catch (e) { /* silent */ }
        }
      }

      isLoggedIn.value = true;
      _runProfileMigration().catch(() => {});
      enableNotifications({ promptIfNeeded: false }).catch(() => {});

      return { isNewAccount };
    } catch (error) {
      console.error("Apple Auth Firestore Error", error);
      await signOut(auth);
      isLoggedIn.value = false;
      user.value = null;

      if (error.code === 'unavailable' || error.message?.includes('offline')) {
        throw new Error("Connexion impossible. Vérifie ta connexion internet.");
      }
      throw error;
    }
  }

  async function registerWithEmail(payload) {
    if (!firebaseReady || !auth || !db) {
      throw new Error("Le service d'authentification est indisponible.");
    }

    try {
        const result = await createUserWithEmailAndPassword(auth, payload.email, payload.password);
        const profile = {
          uid: result.user.uid,
          // Le rattachement entreprise passe par joinCompanyByCode juste apres
          // (les regles exigent companyId null a la creation du compte).
          companyId: null,
          firstName: String(payload.firstname || '').trim(),
          lastName: String(payload.lastname || '').trim(),
          email: payload.email,
          type: 'b2c',
          notifications: {
            email: true,
            email_confirmations: true,
            email_promotions: false,
            push: true,
            push_commandes: true,
            push_promotions: true,
            push_rappels: true
          },
          preferences: {
            regime: payload.regime,
            allergies: normalizeAllergies(payload.allergies),
            aversions: payload.aversions
          },
          address: payload.address,
          phone: payload.phone,
          acquisitionSource: payload.acquisitionSource || '',
          usedReferralCode: payload.referralCode ? payload.referralCode.toUpperCase() : null, // Store used referral code
          cgvAccepted: payload.cgvAccepted || false,
          cgvAcceptedAt: serverTimestamp(),
          platform: SIGNUP_PLATFORM,
          appVersion: APP_VERSION,
          createdAt: serverTimestamp()
        };

        await setDoc(doc(db, 'users', result.user.uid), profile, { merge: true });
        isLoggedIn.value = true;
        user.value = profile;
        trackEvent('sign_up', { method: 'email' });

        _runProfileMigration().catch(() => {});
        // Trigger Notification Permission Prompt
        enableNotifications({ promptIfNeeded: false }).catch(err => console.error("Notification setup failed", err));
        // Même forme de retour que les connexions sociales : un compte créé
        // par email est toujours neuf.
        return { isNewAccount: true };
    } catch (error) {
         throw new Error(mapAuthError(error));
    }
  }

  // Renvoie le jeton push enregistre sur le profil, ou null si la permission
  // est refusee ou si l'enregistrement a echoue : l'appelant ne doit pas
  // afficher les notifications comme actives sans jeton.
  async function enableNotifications(opts = {}) {
      if (!user.value) return null;

      try {
        const { requestPushToken } = await import('../utils/notifications');
        const token = await requestPushToken(opts);

        if (token) {
            console.log('Push Token Granted:', token);
            await setDoc(doc(db, 'users', user.value.uid), {
                fcmToken: token,
                fcmTokens: arrayUnion(token),
                notificationsEnabled: true
            }, { merge: true });
            return token;
        }
      } catch (err) {
          console.error("Error requesting notification permission", err);
      }
      return null;
  }

  async function resetPassword(email) {
    if (!firebaseReady || !auth) {
      throw new Error("Le service d'authentification est indisponible.");
    }
    await sendPasswordResetEmail(auth, email);
  }

  async function loadUserProfile(uid, email) {
    if (!db) {
      return { uid, email, type: 'b2c' };
    }

    const snapshot = await getDoc(doc(db, 'users', uid));
    let profile = { uid, email, type: 'b2c' };

    if (snapshot.exists()) {
      profile = { uid, email, ...snapshot.data() };
    }

    // Fetch company name if companyId present but name missing
    if (profile.companyId && !profile.companyName) {
        try {
            const compSnap = await getDoc(doc(db, 'companies', profile.companyId));
            if (compSnap.exists()) {
                profile.companyName = compSnap.data().name;
            }
        } catch (e) {
            console.error("Failed to load company name", e);
        }
    }

    return profile;
  }

  async function updateProfile(data) {
    if (!firebaseReady || !auth || !db || !user.value) {
      throw new Error("Service indisponible ou compte non connecté.");
    }

    const uid = user.value.uid;
    // Update local state instantly for UI responsiveness
    user.value = { ...user.value, ...data };

    // Update Firestore
    await setDoc(doc(db, 'users', uid), data, { merge: true });
  }

  // Rattache le compte courant a une entreprise via son code d'invitation.
  // users.companyId n'est plus modifiable depuis le client : la callable
  // joinCompanyByCode fait la verification et l'ecriture.
  async function joinCompanyByCode(code) {
    if (!firebaseReady || !functions || !user.value?.uid) {
      throw new Error("Service indisponible ou compte non connecté.");
    }
    const fn = httpsCallable(functions, 'joinCompanyByCode');
    const res = await fn({ code: String(code || '').toUpperCase().trim() });
    const joined = res?.data || {};
    if (!joined.id) throw new Error('Code entreprise invalide.');
    user.value = { ...user.value, companyId: joined.id, companyName: joined.name || '', type: 'b2b-employee' };
    return { id: joined.id, name: joined.name || '' };
  }

  async function refreshProfile() {
    if (!firebaseReady || !auth || !user.value?.uid) return;
    const profile = await loadUserProfile(user.value.uid, user.value.email);
    user.value = profile;
  }

  function checkAuth() {
    if (!firebaseReady || !auth) {
      isAuthReady.value = true;
      return;
    }

    if (authUnsubscribe) return;

    authUnsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // isAuthReady doit TOUJOURS finir a true : sinon le routeur et les pages
      // du compte attendent sans fin quand le profil Firestore ne charge pas
      // (hors ligne, regles refusees). En cas d'echec on garde un profil
      // minimal issu de Firebase Auth.
      try {
        if (firebaseUser) {
          let profile;
          try {
            profile = await loadUserProfile(firebaseUser.uid, firebaseUser.email);
          } catch (e) {
            console.warn('[auth] loadUserProfile failed, profil minimal conserve:', e?.code || e?.message || e);
            profile = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || '',
              type: 'b2c',
              profileLoadFailed: true
            };
          }
          isLoggedIn.value = true;
          user.value = profile;
          localStorage.setItem('isLoggedIn', 'true'); // Keep simple valid flag

          _runProfileMigration().catch(() => {});
          // Register Push Token on session restore
          enableNotifications({ promptIfNeeded: false }).catch(() => {});
        } else {
          try {
            useProfilesStore().unbind();
          } catch (e) {}
          isLoggedIn.value = false;
          user.value = null;
          localStorage.removeItem('isLoggedIn');
        }
      } catch (e) {
        console.error('[auth] onAuthStateChanged handler failed:', e);
      } finally {
        isAuthReady.value = true;
      }
    });
  }

  const isAdmin = computed(() => user.value?.role === 'admin');

  return {
    isLoggedIn,
    isAdmin,
    user,
    isAuthReady,
    login,
    logout,
    loginWithEmail,
    loginWithGoogle,
    loginWithApple,
    registerWithEmail,
    updateProfile,
    joinCompanyByCode,
    refreshProfile,
    resetPassword,
    checkAuth,
    enableNotifications
  };
});
